import React, {useMemo} from "react";
import {QRCodeCanvas} from "qrcode.react";
import {useTranslation} from "react-i18next";
import jsPDF from "jspdf";
import {QUERY_PARAMS} from "../../constants/queryParams";

interface QrCodeCardProps {
    code: string;
    codeType: string;
    qrDestinationPage: string;
}

const CODE_LABELS: Record<string, string> = {
    [QUERY_PARAMS.ACCOUNT]: "Account Code",
    [QUERY_PARAMS.TOUR]: "Tour Code",
    [QUERY_PARAMS.GROUP]: "Group Code"
};

export default function QrCodeCard({code, codeType, qrDestinationPage}: QrCodeCardProps) {
    const {t} = useTranslation();
    const url = `${window.location.origin}/${qrDestinationPage}?${codeType}=${code}`;
    const qrSize = 200;
    const logoSize = qrSize * 0.2; // 20%

    const getQrColors = () => {
        const styles = getComputedStyle(document.documentElement);
        return {
            fg: styles.getPropertyValue("--color-black").trim() || "#000000",
            bg: styles.getPropertyValue("--color-white").trim() || "#ffffff",
            primary: styles.getPropertyValue("--color-primary").trim() || "#0066ff",
            accent: styles.getPropertyValue("--color-success-border").trim() || "#00aa55",
        };
    };

    const {fg, bg, primary, accent} = getQrColors();

    // Logo-SVG als String + data-URL (mit weißem Hintergrundkreis)
    const logoDataUrl = useMemo(() => {
        const svg = `
            <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="30" fill="white"/>
                <path
                    d="M12 48L32 16L52 48"
                    stroke="${primary}"
                    stroke-width="6"
                    stroke-linecap="round"
                    fill="none"
                />
                <circle cx="32" cy="16" r="5" fill="${accent}" />
            </svg>
        `.trim();

        return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    }, [primary, accent]);

    const handleDownloadPdf = () => {
        const canvas = document.querySelector(".qr-code") as HTMLCanvasElement | null;
        if (!canvas) return;

        const dataUrl = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
        });

        const pageWidth = pdf.internal.pageSize.getWidth();

        pdf.setFontSize(22);
        pdf.text(t("common.button.showQr"), pageWidth / 2, 30, {align: "center"});

        const qrSize = 120;
        const qrX = (pageWidth - qrSize) / 2;
        const qrY = 50;

        pdf.addImage(dataUrl, "PNG", qrX, qrY, qrSize, qrSize);

        pdf.setFontSize(14);
        pdf.text(url, pageWidth / 2, qrY + qrSize + 20, {
            align: "center",
            maxWidth: pageWidth - 40,
        });

        pdf.save(`${codeType}-${code}-qr-code.pdf`);
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
        }
    };

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(code);
        } catch {
        }
    };

    return (
        <div className="qr-card">
            <div className="qr-print-area">
                <QRCodeCanvas
                    value={url}
                    size={qrSize}
                    fgColor={fg}
                    bgColor={bg}
                    className="qr-code"
                    imageSettings={{
                        src: logoDataUrl,
                        height: logoSize,
                        width: logoSize,
                        excavate: true
                    }}
                />
            </div>

            <div className="qr-code-info">
                <p>
                    <strong>{CODE_LABELS[codeType]}:</strong> {code}
                </p>
            </div>

            <div className="qr-actions">
                <button className="form-button" onClick={handleDownloadPdf}>
                    {t("common.button.download")}
                </button>

                <button className="form-button-secondary" onClick={handleCopyUrl}>
                    {t("common.button.copyURL")}
                </button>

                <button className="form-button-secondary" onClick={handleCopyCode}>
                    {t("common.button.copyCode")}
                </button>
            </div>
        </div>
    );
}